import classNames from 'classnames';
import { IconV1 } from 'common/icon/iconV1.js';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import { PRIMARY_MENU, PRIMARY_MENU_SKI, SECONDARY_MENU } from 'components/navigation/config.js';
import { isSectionUrl } from 'components/navigation/is-current-url.js';
import { ScreenSizeContext } from 'page-context/column-layout/column-layout-context.js';
import { useContext, useEffect, useState } from 'react';
import { REDUX_APP_SET_OVERLAY_CLOSED, REDUX_APP_SET_OVERLAY_OPEN } from 'redux-state/app/reducer.js';
import { useAppDispatch, useAppSelector } from 'redux-state/configure-store.js';
import { trackNavigateEvent } from 'utilities/posthog-tracking';
import { isThreeCol } from 'utilities/media-queries.js';
import { Pages } from 'utilities/router/api-fetch-path.js';
import { NavMenuItemList } from './nav-menu-item-list.js';
import { NavMenuItemOnClick } from './nav-menu-item.js';

export const PrimaryNavigation = () => {
  const isSkiSeason = useAppSelector((s) => s.LAYOUT.screen?.isSkiSeason);
  return <PrimaryNavigationCore isSkiSeason={isSkiSeason} />;
};

const PrimaryNavigationCore = ({ isSkiSeason }: { isSkiSeason?: string }) => {
  const appDispatch = useAppDispatch();

  const isDesktopOrAbove = isThreeCol(useContext(ScreenSizeContext)); // === ': state.WINDOW.isDesktopOrAbove,
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const dropdownOpenClass = { 'is-open': dropdownIsOpen };
  const mobileMenuOpenClass = { 'is-open': menuIsOpen };
  const menuItems = isSkiSeason === 'true' ? PRIMARY_MENU_SKI : PRIMARY_MENU;

  useEffect(() => {
    if (isDesktopOrAbove) {
      setMenuIsOpen(false);
      setDropdownIsOpen(false);
    }
  }, [isDesktopOrAbove]);

  const shutMenu = () => {
    appDispatch(REDUX_APP_SET_OVERLAY_CLOSED());
    setMenuIsOpen(false);
  };

  const navToggleClicked = () => {
    if (menuIsOpen) {
      appDispatch(REDUX_APP_SET_OVERLAY_CLOSED());
    } else {
      appDispatch(REDUX_APP_SET_OVERLAY_OPEN());
    }
    setMenuIsOpen((c) => !c);
    appDispatch(REDUX_APP_SET_OVERLAY_CLOSED());
  };

  const toggleDropdownMenu = () => {
    setDropdownIsOpen((c) => !c);
  };

  const dropdownMenuItemClicked: NavMenuItemOnClick = ({ item }) => {
    trackNavigateEvent('site_header', item.name + "_tab");
    setDropdownIsOpen(false);
  };

  const onPrimaryMenuItemClick: NavMenuItemOnClick = ({ item }) => {
    trackNavigateEvent('site_header', item.name + "_tab");
    shutMenu();  // mobile menu
    setDropdownIsOpen(false);  // "More" menu on desktop
  };

  const onLogoClick = () => {
    trackNavigateEvent('site_header', 'metservice_logo');
  };

  return (
    <nav className="Nav Nav--primary">
      <button className={classNames('Nav-toggle', mobileMenuOpenClass)} onClick={navToggleClicked}>
        <span className="u-hiddenVisually">Menu</span>
        <i></i>
        <i></i>
        <i></i>
      </button>

      <div className="Container">
        <div className="Nav-group Nav-group--links">
          <LinkFactoryReactRouter className="Nav-logo" href={Pages.HOME} onClick={onLogoClick}>
            <IconV1 icon="main-logo" />
            <span className="u-hiddenVisually">MetService - Te Ratonga Tirorangi</span>
          </LinkFactoryReactRouter>
          <div className="Nav-menu">
            <ul className="Nav-menu-list">
              <NavMenuItemList
                menu={menuItems}
                className="Nav-menu-list-item"
                onClick={onPrimaryMenuItemClick}
                activeCB={({ linkUrl, currentUrl }) =>
                  isSectionUrl({
                    currentUrl,
                    linkUrl,
                  })
                }
              />
            </ul>
          </div>
        </div>

        <div className="Nav-group Nav-group--more">
          <div className={classNames('Nav-dropdown', dropdownOpenClass)}>
            <button className="Nav-dropdown-trigger u-flex u-flexAlignItemsCenter" onClick={toggleDropdownMenu}>
              <span className="u-flex-1">More </span>
              <span className="u-span-width u-flexGrow-0 u-flexAlignItemsCenter"></span>
              <IconV1
                icon={`${dropdownIsOpen ? 'chevron-up' : 'chevron-down'}`}
                iconClass="svg--fill-inherit svg--1em"
              />
            </button>
            <ul className="Nav-dropdown-target">
              <NavMenuItemList
                menu={SECONDARY_MENU}
                onClick={dropdownMenuItemClicked}
                className="Nav-dropdown-target-item"
                activeCB={({ linkUrl, currentUrl }) =>
                  isSectionUrl({
                    currentUrl,
                    linkUrl,
                  })
                }
              />
            </ul>
          </div>
        </div>
      </div>

      <div className={classNames('MobileMenu', mobileMenuOpenClass)}>
        <div className="MobileMenu-scroll">
          <ul className="MobileMenu-list">
            <NavMenuItemList
              menu={menuItems}
              className="MobileMenu-list-item"
              onClick={onPrimaryMenuItemClick}
              activeCB={({ linkUrl, currentUrl }) =>
                isSectionUrl({
                  currentUrl,
                  linkUrl,
                })
              }
            />
          </ul>
          <ul className="MobileMenu-list MobileMenu-list--secondary">
            <NavMenuItemList
              menu={SECONDARY_MENU}
              className="MobileMenu-list-item"
              onClick={shutMenu}
              activeCB={({ linkUrl, currentUrl }) =>
                isSectionUrl({
                  currentUrl,
                  linkUrl,
                })
              }
            />
          </ul>
        </div>
      </div>
    </nav>
  );
};
